import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight, Crown, Flower2, Sparkles } from 'lucide-react'
import { Ornament } from '@/components/generals/ornament'
import { WhatsAppButton, buttonClass } from '@/components/generals/cta-buttons'
import { whatsappMessages } from '@/constants/site'

const bridalFeatures = [
    {
        title: "HD Bridal Makeup",
        text: "A lightweight, skin-like finish that looks natural in person and stunning in HD photos.",
        icon: Sparkles
    },
    {
        title: "Airbrush Bridal Makeup",
        text: "A smooth, even base applied in fine layers for a soft-focus, flawless look.",
        icon: Crown
    },
    {
        title: "Bridal Hair & Draping",
        text: "Buns, braids with flowers, dupatta setting and neat lehenga or saree draping.",
        icon: Flower2
    }
]

export const BridalSpotlight: FC = (): ReactNode => {
    return (
        <section
            aria-labelledby='bridal-title'
            className='plum-glow relative isolate overflow-hidden py-20 text-ivory sm:py-24'
        >
            <div aria-hidden='true' className='pointer-events-none absolute -right-32 top-0 -z-10 size-96 rounded-full bg-gold/10 blur-3xl' />

            <div className='mx-auto max-w-7xl px-5 text-center sm:px-8'>
                <p className='gold-shine inline-block px-3 pb-2 font-script text-4xl leading-tight sm:text-5xl'>
                    Bride to be?
                </p>

                <h2 id='bridal-title' className='mx-auto mt-2 max-w-3xl font-heading text-[1.7rem] font-semibold leading-tight sm:text-4xl'>
                    HD &amp; Airbrush Bridal Looks, made just for you
                </h2>

                <Ornament className='mx-auto mt-5' />

                <p className='mx-auto mt-5 max-w-2xl text-base leading-relaxed text-ivory/75 sm:text-lg'>
                    Your wedding look is planned around your outfit, jewellery, skin type and ceremony timings. We also create looks for your engagement, mehendi, sangeet and reception, and do makeup for your family and bridesmaids.
                </p>

                <ul className='mt-12 grid gap-4 text-left md:grid-cols-3'>
                    {bridalFeatures.map(feature => (
                        <li
                            key={feature.title}
                            className='rounded-2xl border border-gold/25 bg-white/[0.04] p-6 backdrop-blur-sm transition-colors hover:border-gold/50'
                        >
                            <span className='gold-gradient flex size-11 items-center justify-center rounded-xl text-plum-deep'>
                                <feature.icon className='size-5' aria-hidden='true' />
                            </span>

                            <h3 className='mt-4 font-heading text-base font-semibold text-gold-light'>
                                {feature.title}
                            </h3>

                            <p className='mt-2 text-sm leading-relaxed text-ivory/75'>
                                {feature.text}
                            </p>
                        </li>
                    ))}
                </ul>

                <div className='mt-10 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center'>
                    <WhatsAppButton
                        message={whatsappMessages.bridal}
                        label='Plan my bridal look'
                        variant='gold'
                    />

                    <Link href='/services/bridal-makeup' className={buttonClass('outline-light')}>
                        Bridal makeup details
                        <ArrowRight className='size-4' aria-hidden='true' />
                    </Link>
                </div>
            </div>
        </section>
    )
}
